/**
 * Auto-atendimento LGPD Art. 18: criar e acompanhar solicitações de dados.
 */
import { FormEvent, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { lgpdApi, getErrorMessage } from '../../lib/api'
import { PageLoading } from '../../components/LoadingSpinner'
import type { LgpdRequestStatus, LgpdRequestType } from '../../types/api'

const TYPE_LABELS: Record<LgpdRequestType, string> = {
  ACESSO: 'Confirmação e acesso aos meus dados',
  CORRECAO: 'Correção de dados incorretos',
  ELIMINACAO: 'Eliminação de dados desnecessários',
  PORTABILIDADE: 'Portabilidade dos meus dados',
  REVOGACAO_CONSENTIMENTO: 'Revogação do consentimento',
}

const STATUS_BADGE: Record<LgpdRequestStatus, { label: string; cls: string }> = {
  PENDENTE: { label: 'Pendente', cls: 'badge-yellow' },
  EM_ANALISE: { label: 'Em análise', cls: 'badge-blue' },
  CONCLUIDO: { label: 'Concluído', cls: 'badge-green' },
  INDEFERIDO: { label: 'Indeferido', cls: 'badge-gray' },
}

export function SolicitacoesPage() {
  const qc = useQueryClient()
  const [tipo, setTipo] = useState<LgpdRequestType>('ACESSO')
  const [justificativa, setJustificativa] = useState('')
  const [formError, setFormError] = useState<string | null>(null)
  const [showForm, setShowForm] = useState(false)

  const { data: requests, isLoading } = useQuery({
    queryKey: ['lgpd', 'requests'],
    queryFn: lgpdApi.list,
  })

  const mutation = useMutation({
    mutationFn: () => lgpdApi.create(tipo, justificativa || undefined),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['lgpd', 'requests'] })
      setShowForm(false)
      setJustificativa('')
      setFormError(null)
    },
    onError: (err) => setFormError(getErrorMessage(err)),
  })

  function handleSubmit(e: FormEvent) {
    e.preventDefault()
    setFormError(null)
    mutation.mutate()
  }

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 'var(--space-6)' }}>
        <div>
          <h1 style={{ fontSize: 'var(--font-size-2xl)', fontWeight: 700 }}>Minhas Solicitações</h1>
          <p style={{ color: 'var(--color-gray-600)', fontSize: 'var(--font-size-sm)' }}>
            Exercite seus direitos LGPD Art. 18
          </p>
        </div>
        <button className="btn btn-primary" onClick={() => setShowForm(!showForm)}>
          {showForm ? 'Cancelar' : '+ Nova solicitação'}
        </button>
      </div>

      {/* Formulário */}
      {showForm && (
        <div className="card" style={{ marginBottom: 'var(--space-6)' }}>
          <h2 style={{ fontSize: 'var(--font-size-lg)', fontWeight: 600, marginBottom: 'var(--space-4)' }}>
            Nova solicitação
          </h2>
          {formError && <div className="alert alert-danger" style={{ marginBottom: 'var(--space-4)' }}>{formError}</div>}
          <form onSubmit={handleSubmit} noValidate>
            <div style={{ marginBottom: 'var(--space-4)' }}>
              <label htmlFor="tipo" style={{ display: 'block', fontSize: 'var(--font-size-sm)', fontWeight: 500, marginBottom: 'var(--space-1)' }}>
                Tipo de solicitação
              </label>
              <select
                id="tipo"
                className="input"
                value={tipo}
                onChange={(e) => setTipo(e.target.value as LgpdRequestType)}
              >
                {(Object.keys(TYPE_LABELS) as LgpdRequestType[]).map((t) => (
                  <option key={t} value={t}>{TYPE_LABELS[t]}</option>
                ))}
              </select>
            </div>
            <div style={{ marginBottom: 'var(--space-4)' }}>
              <label htmlFor="justificativa" style={{ display: 'block', fontSize: 'var(--font-size-sm)', fontWeight: 500, marginBottom: 'var(--space-1)' }}>
                Observação (opcional)
              </label>
              <textarea
                id="justificativa"
                className="input"
                value={justificativa}
                onChange={(e) => setJustificativa(e.target.value)}
                rows={3}
                maxLength={1000}
                placeholder="Descreva o que você precisa…"
                style={{ resize: 'vertical' }}
              />
            </div>
            <button type="submit" className="btn btn-primary" disabled={mutation.isPending}>
              {mutation.isPending ? 'Enviando…' : 'Enviar solicitação'}
            </button>
          </form>
        </div>
      )}

      {/* Lista */}
      <div className="card" style={{ padding: isLoading || !requests?.length ? 'var(--space-6)' : 0 }}>
        {isLoading ? (
          <PageLoading />
        ) : !requests?.length ? (
          <p style={{ textAlign: 'center', color: 'var(--color-gray-400)', fontSize: 'var(--font-size-sm)' }}>
            Você ainda não tem solicitações.
          </p>
        ) : (
          <div className="table-wrapper">
            <table>
              <thead>
                <tr>
                  <th>Tipo</th>
                  <th>Status</th>
                  <th>Data</th>
                  <th>Resposta</th>
                </tr>
              </thead>
              <tbody>
                {requests.map((r) => {
                  const badge = STATUS_BADGE[r.status]
                  return (
                    <tr key={r.id}>
                      <td style={{ fontSize: 'var(--font-size-sm)' }}>{TYPE_LABELS[r.tipo]}</td>
                      <td><span className={`badge ${badge.cls}`}>{badge.label}</span></td>
                      <td style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-gray-600)' }}>
                        {new Date(r.criado_em).toLocaleDateString('pt-BR')}
                      </td>
                      <td style={{ fontSize: 'var(--font-size-sm)', maxWidth: '240px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {r.resposta ?? '—'}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}
